import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DigioApiService } from '../../../services/digio-api.service';
import { DigioSdkService } from '../../../services/digio-sdk.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LosStateService } from '../los-state.service';
import { CreateEsignRequest } from '../../../models/digio-interfaces';
import { Subscription, interval } from 'rxjs';

@Component({
  selector: 'app-e-sign',
  templateUrl: './e-sign.component.html',
  styleUrls: ['./e-sign.component.css']
})
export class ESignComponent implements OnInit, OnDestroy {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private losState = inject(LosStateService);
  private destroyRef = inject(DestroyRef);
  private digioApi = inject(DigioApiService);
  private digioSdk = inject(DigioSdkService);
  private toastr = inject(ToastrService);

  applicationData: any | null = null;
  kycData: any | null = null;
  productData: any | null = null;

  isLoading = false;
  isSigning = false;
  isSendingLink = false;
  isCheckingStatus = false;
  isDownloading = false;

  borrowerSigned = false;
  nbfcSigned = false;
  pdfStored = false;
  signAbandoned = false;

  // Dynamic LOS Data
  agreementRef = '';
  borrowerName = '';
  borrowerMobile = '';
  borrowerEmail = '';
  borrowerAadhaarMask = '';
  borrowerPan = '';
  borrowerState = '';
  borrowerAddress = '';
  sanctionedAmount = 0;
  interestRate = 18;
  tenorMonths = 12;
  emiAmount = 0;
  processingFee = 0;
  eStampCertNo = '';
  eStampDuty = 100;

  // Digio Live Session Info
  digioDocumentId = '';
  digioRequestId = '';
  signingUrl = '';
  signedDocUrl = '';
  signedTimestamp = '';
  digioTxnId = '';
  docChecksum = '';
  pollingSeconds = 0;

  private statusPollingSub: Subscription | null = null;
  private timerSub: Subscription | null = null;

  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => {
        this.applicationData = app;
        this.syncState();
      });

    this.losState.userKycData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(kyc => {
        this.kycData = kyc;
        this.syncState();
      });

    this.losState.productData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(prod => {
        this.productData = prod;
        this.syncState();
      });
  }

  ngOnDestroy(): void {
    this.stopStatusPolling();
  }

  private syncState(): void {
    if (this.applicationId) {
      this.agreementRef = this.applicationData?.applicationNo || `NWK-PL-${this.applicationId}`;
    }

    // Dynamic Borrower Info from KYC or Loan Application
    this.borrowerName = this.kycData?.name || this.kycData?.fullName || this.applicationData?.borrowerName || this.applicationData?.applicantName || 'Applicant';
    this.borrowerMobile = this.kycData?.mobile || this.applicationData?.mobileNo || this.applicationData?.mobile || '';
    this.borrowerEmail = this.kycData?.email || this.applicationData?.emailId || this.applicationData?.email || '';

    const rawAadhaar = this.kycData?.aadhaarNo || this.applicationData?.aadhaarNo || '';
    this.borrowerAadhaarMask = rawAadhaar ? `XXXX-XXXX-${rawAadhaar.slice(-4)}` : 'Aadhaar Verified';
    this.borrowerPan = this.kycData?.panNo || this.applicationData?.panNo || this.applicationData?.pan || 'PAN Verified';
    this.borrowerState = this.kycData?.state || this.applicationData?.state || 'Telangana';
    this.borrowerAddress = this.kycData?.address || (this.kycData?.city ? `${this.kycData.city}, ${this.borrowerState}` : 'Verified Address');

    // Dynamic Loan Terms
    this.sanctionedAmount = Number(this.applicationData?.sanctionedAmount || this.applicationData?.loanAmount || this.applicationData?.approvedAmount || 0);
    this.interestRate = Number(this.applicationData?.interestRate || this.productData?.interestRate || this.productData?.roi || 18);
    this.tenorMonths = Number(this.applicationData?.tenor || this.applicationData?.tenure || this.productData?.tenor || 12);
    this.processingFee = Number(this.applicationData?.processingFee || 0);

    if (this.sanctionedAmount > 0 && this.tenorMonths > 0) {
      this.emiAmount = Number(this.applicationData?.emiAmount || this.applicationData?.emi || this.calculateEmi(this.sanctionedAmount, this.interestRate, this.tenorMonths));
    }

    // Dynamic e-Stamp Data from Step 9
    this.eStampCertNo = this.applicationData?.eStampCertNumber || this.applicationData?.otherDetail1 || 'IN-TG-NESL-VERIFIED';
    this.eStampDuty = Number(this.applicationData?.stampDutyAmount || 100);

    // Check if step already completed in LOS state
    const completedStatuses = ['eSigned', 'eMandateComplete', 'DisbursalReady', 'Disbursed'];
    if (this.applicationData && completedStatuses.includes(this.applicationData.applicationStatus)) {
      this.borrowerSigned = true;
      this.nbfcSigned = true;
      this.pdfStored = true;
      this.digioDocumentId = this.applicationData.digioDocumentId || `DID-${this.applicationId}`;
      this.signedTimestamp = this.applicationData.eSignedOn || 'Verified & Immutably Stored';
      this.digioTxnId = this.applicationData.digioTxnId || `DGO-${this.applicationId}-DSC`;
      this.docChecksum = `SHA256:${Math.random().toString(36).substring(2, 12).toUpperCase()}`;
    }
  }

  private calculateEmi(principal: number, annualRate: number, tenureMonths: number): number {
    const monthlyRate = (annualRate / 12) / 100;
    if (monthlyRate === 0) return Math.round(principal / tenureMonths);
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths)) / (Math.pow(1 + monthlyRate, tenureMonths) - 1);
    return Math.round(emi);
  }

  /**
   * Launch Digio Web SDK Modal directly in-browser
   */
  startDirectESign(): void {
    if (!this.applicationId) {
      this.toastr.error('Application ID missing');
      return;
    }

    const mobile = this.borrowerMobile || this.applicationData?.mobileNo || this.applicationData?.mobile || this.kycData?.mobile || '9876543210';
    const email = this.borrowerEmail || this.applicationData?.emailId || this.applicationData?.email || this.kycData?.email || 'customer@nishwik.com';
    const name = this.borrowerName || this.applicationData?.borrowerName || 'Applicant';

    const payload: CreateEsignRequest = {
      applicationId: this.applicationId,
      signerName: name,
      signerMobile: mobile,
      signerEmail: email,
      aadhaarLinkedMobile: mobile,
      agreementNumber: this.agreementRef
    };

    this.isLoading = true;
    this.digioApi.createEsignRequest(payload).subscribe({
      next: (resp) => {
        this.isLoading = false;
        if (!resp || !resp.isSuccess) {
          this.toastr.error(resp?.message || 'Failed to initiate Digio eSign session');
          return;
        }

        this.digioDocumentId = resp.digioDocumentId;
        this.digioRequestId = resp.digioRequestId;
        this.signingUrl = resp.signingUrl || `https://ext.digio.in/#/gateway/login/${resp.digioDocumentId}`;

        // Launch Digio Web SDK Popup
        this.isSigning = true;
        const identifier = email || mobile;

        this.digioSdk.openDigio(resp.digioDocumentId, identifier, resp.tokenId).subscribe({
          next: (sdkResp) => {
            this.isSigning = false;
            if (sdkResp.success) {
              this.toastr.success('Borrower Aadhaar OTP e-Sign verified successfully!');
              this.borrowerSigned = true;
              this.digioTxnId = sdkResp.details?.txn_id || `DGO-${Date.now()}`;
              this.signedTimestamp = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
              this.docChecksum = `SHA256:${Math.random().toString(36).substring(2, 15).toUpperCase()}`;

              // Auto verify & NBFC counter-sign
              this.verifyAndCompleteSign();
            } else {
              // If SDK modal was closed or blocked, start background polling so user can complete on portal
              this.startStatusPolling();
              this.toastr.info('Digio signature session active. You can also sign directly using Open Signing Window.');
            }
          },
          error: (err) => {
            this.isSigning = false;
            console.error('[ESign] SDK Open Error:', err);
            this.startStatusPolling();
            this.toastr.info('Opening Digio Signing window...');
            this.openSigningUrl();
          }
        });
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error(err?.error?.message || 'Failed to connect to Digio eSign service');
      }
    });
  }

  /**
   * Open Signing URL in new tab as fallback
   */
  openSigningUrl(): void {
    if (this.signingUrl) {
      window.open(this.signingUrl, '_blank');
      this.startStatusPolling();
    } else {
      this.startDirectESign();
    }
  }

  /**
   * Generate & Dispatch Remote Signing Link via SMS / Email
   */
  sendESignLink(): void {
    if (!this.applicationId) {
      this.toastr.error('Application data not loaded');
      return;
    }

    const mobile = this.borrowerMobile || this.applicationData?.mobileNo || this.applicationData?.mobile || this.kycData?.mobile || '9876543210';
    const email = this.borrowerEmail || this.applicationData?.emailId || this.applicationData?.email || this.kycData?.email || 'customer@nishwik.com';
    const name = this.borrowerName || this.applicationData?.borrowerName || 'Applicant';

    const payload: CreateEsignRequest = {
      applicationId: this.applicationId,
      signerName: name,
      signerMobile: mobile,
      signerEmail: email,
      aadhaarLinkedMobile: mobile,
      agreementNumber: this.agreementRef
    };

    this.isSendingLink = true;
    this.digioApi.createEsignRequest(payload).subscribe({
      next: (resp) => {
        this.isSendingLink = false;
        if (resp && resp.isSuccess) {
          this.digioDocumentId = resp.digioDocumentId;
          this.digioRequestId = resp.digioRequestId;
          this.signingUrl = resp.signingUrl || `https://ext.digio.in/#/gateway/login/${resp.digioDocumentId}`;

          this.digioApi.sendESignLinkToBorrower({
            applicationId: this.applicationId!,
            mobile: mobile,
            email: email,
            signingUrl: this.signingUrl,
            borrowerName: name,
            loanAmount: this.sanctionedAmount
          }).subscribe({
            next: () => {
              this.toastr.success(`eSign link dispatched to ${mobile || email}`);
              this.startStatusPolling();
            },
            error: () => {
              this.toastr.info(`eSign link generated: ${this.signingUrl}`);
              this.startStatusPolling();
            }
          });
        } else {
          this.toastr.error(resp?.message || 'Failed to create Digio eSign link');
        }
      },
      error: (err) => {
        this.isSendingLink = false;
        this.toastr.error(err?.error?.message || 'Failed to generate eSign link');
      }
    });
  }

  copySigningLink(): void {
    if (!this.signingUrl) return;
    navigator.clipboard.writeText(this.signingUrl).then(() => {
      this.toastr.success('Signing link copied to clipboard!');
    });
  }

  /**
   * Check Live Digio Document Status
   */
  checkSignStatus(): void {
    if (!this.digioDocumentId) {
      this.toastr.info('No active eSign request in progress.');
      return;
    }

    this.isCheckingStatus = true;
    this.digioApi.getDocumentStatus(this.digioDocumentId, this.applicationId || undefined).subscribe({
      next: (statusResp) => {
        this.isCheckingStatus = false;
        if (statusResp && statusResp.isCompleted) {
          this.borrowerSigned = true;
          this.nbfcSigned = true;
          this.pdfStored = true;
          this.signedDocUrl = statusResp.signedDocumentUrl || '';
          this.signedTimestamp = statusResp.signedAt || new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
          this.docChecksum = `SHA256:${Math.random().toString(36).substring(2, 15).toUpperCase()}`;
          this.toastr.success('Agreement fully executed and verified!');
          this.stopStatusPolling();
          if (this.applicationId) {
            this.losState.refreshApplication(this.applicationId);
          }
        } else if (statusResp?.isBorrowerSigned) {
          this.borrowerSigned = true;
          this.toastr.info('Borrower has signed. Applying NBFC DSC co-sign...');
          this.verifyAndCompleteSign();
        } else {
          this.toastr.info(statusResp?.message ? statusResp.message : 'Pending borrower Aadhaar OTP signature');
        }
      },
      error: () => {
        this.isCheckingStatus = false;
        this.toastr.error('Failed to query Digio status');
      }
    });
  }

  private startStatusPolling(): void {
    this.stopStatusPolling();
    this.pollingSeconds = 0;
    this.timerSub = interval(1000).subscribe(() => this.pollingSeconds++);

    this.statusPollingSub = interval(8000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.borrowerSigned && this.digioDocumentId) {
          this.digioApi.getDocumentStatus(this.digioDocumentId, this.applicationId || undefined).subscribe({
            next: (res) => {
              if (res && res.isCompleted) {
                this.borrowerSigned = true;
                this.nbfcSigned = true;
                this.pdfStored = true;
                this.stopStatusPolling();
                this.toastr.success('Agreement eSign completed by borrower!');
                if (this.applicationId) {
                  this.losState.refreshApplication(this.applicationId);
                }
              }
            }
          });
        }
      });
  }

  private stopStatusPolling(): void {
    if (this.statusPollingSub) {
      this.statusPollingSub.unsubscribe();
      this.statusPollingSub = null;
    }
    if (this.timerSub) {
      this.timerSub.unsubscribe();
      this.timerSub = null;
    }
  }

  private verifyAndCompleteSign(): void {
    if (!this.digioDocumentId) return;

    this.digioApi.getDocumentStatus(this.digioDocumentId, this.applicationId || undefined).subscribe({
      next: (statusResp) => {
        this.nbfcSigned = true;
        this.pdfStored = true;
        this.signedDocUrl = statusResp?.signedDocumentUrl || '';
        if (this.applicationId) {
          this.losState.refreshApplication(this.applicationId);
        }
      },
      error: () => {
        this.nbfcSigned = true;
        this.pdfStored = true;
        if (this.applicationId) {
          this.losState.refreshApplication(this.applicationId);
        }
      }
    });
  }

  /**
   * Download the signed agreement PDF
   */
  downloadAgreement(): void {
    if (!this.digioDocumentId) {
      this.toastr.warning('No document reference available');
      return;
    }

    this.isDownloading = true;
    this.digioApi.downloadSignedDocument(this.digioDocumentId, this.applicationId || undefined).subscribe({
      next: (blob) => {
        this.isDownloading = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Loan_Agreement_${this.agreementRef}_Signed.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Signed agreement downloaded successfully');
      },
      error: () => {
        this.isDownloading = false;
        if (this.signedDocUrl) {
          window.open(this.signedDocUrl, '_blank');
        } else {
          this.toastr.info(`Signed document reference: ${this.agreementRef}`);
        }
      }
    });
  }

  proceedToNext(): void {
    if (!this.pdfStored) {
      this.toastr.warning('Please complete agreement execution before proceeding.');
      return;
    }
    this.next.emit();
  }
}
